// client/src/components/shared/orders/PublicOrderStatus.jsx

import React from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import {
  LocalShippingOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';

import {
  useParams,
} from 'react-router-dom';

import {
  tokens,
} from '../../../theme';

import Navbar
  from '../../react-public/global/Navbar';

import Footer
  from '../../react-public/global/Footer';

import {
  useGetPublicOrderStatusQuery,
} from '../../../features/orders/ordersApiSlice';


const siteFont =
  'Poppins, sans-serif';


const formatCurrency =
  (
    cents,
    currency = 'usd'
  ) =>
    new Intl
      .NumberFormat(
        'en-US',
        {
          style:
            'currency',

          currency:
            String(
              currency
            ).toUpperCase(),
        }
      )
      .format(
        Number(
          cents || 0
        ) / 100
      );


const formatStatus =
  (status) =>
    String(
      status ||
      'unfulfilled'
    )
      .replaceAll(
        '_',
        ' '
      )
      .replace(
        /\b\w/g,
        (character) =>
          character.toUpperCase()
      );


const PublicOrderStatus =
  () => {
    const theme =
      useTheme();

    const colors =
      tokens(
        theme.palette.mode
      );


    const {
      token,
    } =
      useParams();


    const {
      data,
      isLoading,
      isError,
      error,
    } =
      useGetPublicOrderStatusQuery(
        token,
        {
          skip:
            !token,
        }
      );


    const order =
      data?.order;


    const fulfillmentStatus =
      order
        ?.fulfillment
        ?.status ||
      'unfulfilled';


    return (
      <Box
        width="100%"
        minHeight="100vh"
        sx={{
          background:
            'radial-gradient(circle at 50% 15%, rgba(0,255,170,0.11), transparent 32%)',
        }}
      >
        <Navbar />


        <Container
          maxWidth="md"
          sx={{
            py: {
              xs:
                6,

              md:
                10,
            },

            fontFamily:
              siteFont,

            '& .MuiTypography-root': {
              fontFamily:
                siteFont,
            },

            '& .MuiButton-root': {
              fontFamily:
                siteFont,
            },
          }}
        >
          <Paper
            elevation={
              0
            }
            sx={{
              width:
                '100%',

              boxSizing:
                'border-box',

              p: {
                xs:
                  3,

                sm:
                  4,

                md:
                  5,
              },

              borderRadius:
                '28px',

              color:
                'white',

              backgroundColor:
                'rgba(18,24,32,0.84)',

              backdropFilter:
                'blur(18px)',

              border:
                '1px solid rgba(255,255,255,0.11)',

              boxShadow:
                '0 30px 90px rgba(0,0,0,0.30)',
            }}
          >
            {/* HEADER */}

            <Box
              textAlign="center"
            >
              <Box
                sx={{
                  width:
                    82,

                  height:
                    82,

                  mx:
                    'auto',

                  borderRadius:
                    '50%',

                  display:
                    'flex',

                  alignItems:
                    'center',

                  justifyContent:
                    'center',

                  backgroundColor:
                    'rgba(0,255,170,0.12)',

                  border:
                    '1px solid rgba(0,255,170,0.30)',
                }}
              >
                <LocalShippingOutlined
                  sx={{
                    fontSize:
                      46,

                    color:
                      colors.greenAccent[400],
                  }}
                />
              </Box>


              <Typography
                variant="h2"
                sx={{
                  mt:
                    3,

                  color:
                    'white',

                  fontWeight:
                    900,

                  letterSpacing:
                    '-0.055em',

                  lineHeight:
                    1,
                }}
              >
                Order Status
              </Typography>


              <Typography
                sx={{
                  mt:
                    1.5,

                  mx:
                    'auto',

                  maxWidth:
                    '650px',

                  color:
                    'rgba(255,255,255,0.72)',

                  lineHeight:
                    1.7,

                  fontSize:
                    '1rem',
                }}
              >
                View your USFuel order,
                fulfillment status, and
                shipping information.
              </Typography>
            </Box>


            <Divider
              sx={{
                my:
                  4,

                borderColor:
                  'rgba(255,255,255,0.10)',
              }}
            />


            {/* LOADING */}

            {isLoading && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                py={
                  8
                }
              >
                <CircularProgress
                  sx={{
                    color:
                      colors.greenAccent[400],
                  }}
                />

                <Typography
                  sx={{
                    mt:
                      2,

                    color:
                      'rgba(255,255,255,0.72)',

                    fontWeight:
                      700,
                  }}
                >
                  Loading your order...
                </Typography>
              </Box>
            )}


            {/* ERROR */}

            {isError && (
              <Alert
                severity="error"
              >
                {error
                  ?.data
                  ?.message ||
                  'This order-status link is invalid or unavailable.'}
              </Alert>
            )}


            {/* ORDER */}

            {order && (
              <>
                <Stack
                  direction={{
                    xs:
                      'column',

                    sm:
                      'row',
                  }}
                  justifyContent="space-between"
                  alignItems={{
                    xs:
                      'flex-start',

                    sm:
                      'center',
                  }}
                  spacing={
                    2
                  }
                >
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color:
                          'white',

                        fontWeight:
                          900,
                      }}
                    >
                      Order{' '}
                      {String(
                        order._id
                      )
                        .slice(
                          -8
                        )
                        .toUpperCase()}
                    </Typography>


                    <Typography
                      sx={{
                        mt:
                          0.5,

                        color:
                          'rgba(255,255,255,0.60)',
                      }}
                    >
                      {new Date(
                        order.createdAt
                      ).toLocaleDateString(
                        'en-US',
                        {
                          year:
                            'numeric',

                          month:
                            'long',

                          day:
                            'numeric',
                        }
                      )}
                    </Typography>
                  </Box>


                  <Chip
                    icon={
                      <LocalShippingOutlined />
                    }
                    label={
                      formatStatus(
                        fulfillmentStatus
                      )
                    }
                    sx={{
                      px:
                        1,

                      height:
                        38,

                      fontWeight:
                        800,

                      color:
                        fulfillmentStatus ===
                        'delivered'
                          ? colors.greenAccent[300]
                          : 'white',

                      backgroundColor:
                        fulfillmentStatus ===
                        'delivered'
                          ? 'rgba(0,255,170,0.12)'
                          : fulfillmentStatus ===
                            'shipped'
                            ? 'rgba(80,150,255,0.14)'
                            : 'rgba(255,255,255,0.08)',

                      border:
                        fulfillmentStatus ===
                        'delivered'
                          ? '1px solid rgba(0,255,170,0.28)'
                          : '1px solid rgba(255,255,255,0.12)',

                      '& .MuiChip-icon': {
                        color:
                          'inherit',
                      },
                    }}
                  />
                </Stack>


                <Divider
                  sx={{
                    my:
                      4,

                    borderColor:
                      'rgba(255,255,255,0.10)',
                  }}
                />


                {/* FULFILLMENT */}

                <Box
                  sx={{
                    p: {
                      xs:
                        2.5,

                      sm:
                        3,
                    },

                    borderRadius:
                      '20px',

                    backgroundColor:
                      'rgba(0,255,170,0.06)',

                    border:
                      '1px solid rgba(0,255,170,0.16)',
                  }}
                >
                  <Typography
                    sx={{
                      color:
                        'rgba(255,255,255,0.58)',

                      fontSize:
                        '0.75rem',

                      fontWeight:
                        800,

                      textTransform:
                        'uppercase',

                      letterSpacing:
                        '0.08em',
                    }}
                  >
                    Fulfillment Status
                  </Typography>


                  <Typography
                    variant="h4"
                    sx={{
                      mt:
                        0.5,

                      color:
                        colors.greenAccent[300],

                      fontWeight:
                        900,
                    }}
                  >
                    {formatStatus(
                      fulfillmentStatus
                    )}
                  </Typography>


                  {order
                    .fulfillment
                    ?.shippedAt && (
                    <Typography
                      sx={{
                        mt:
                          1,

                        color:
                          'rgba(255,255,255,0.62)',

                        fontSize:
                          '0.9rem',
                      }}
                    >
                      Shipped{' '}
                      {new Date(
                        order
                          .fulfillment
                          .shippedAt
                      ).toLocaleDateString()}
                    </Typography>
                  )}


                  {order
                    .fulfillment
                    ?.deliveredAt && (
                    <Typography
                      sx={{
                        mt:
                          0.5,

                        color:
                          'rgba(255,255,255,0.62)',

                        fontSize:
                          '0.9rem',
                      }}
                    >
                      Delivered{' '}
                      {new Date(
                        order
                          .fulfillment
                          .deliveredAt
                      ).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>


                <Divider
                  sx={{
                    my:
                      4,

                    borderColor:
                      'rgba(255,255,255,0.10)',
                  }}
                />


                {/* ITEMS */}

                <Typography
                  variant="h4"
                  sx={{
                    mb:
                      2,

                    color:
                      'white',

                    fontWeight:
                      900,
                  }}
                >
                  Order
                </Typography>


                <Stack
                  spacing={
                    0
                  }
                >
                  {(
                    order.items ||
                    []
                  ).map(
                    (
                      item,
                      index
                    ) => (
                      <Box
                        key={
                          item._id ||
                          index
                        }
                        sx={{
                          py:
                            2,

                          borderBottom:
                            index <
                            order.items.length -
                              1
                              ? '1px solid rgba(255,255,255,0.08)'
                              : 'none',
                        }}
                      >
                        <Stack
                          direction={{
                            xs:
                              'column',

                            sm:
                              'row',
                          }}
                          justifyContent="space-between"
                          spacing={
                            1
                          }
                        >
                          <Box>
                            <Typography
                              sx={{
                                color:
                                  'white',

                                fontWeight:
                                  800,

                                fontSize:
                                  '1rem',
                              }}
                            >
                              {item.quantity}
                              {' × '}
                              {item.name}
                            </Typography>


                            <Typography
                              sx={{
                                mt:
                                  0.4,

                                color:
                                  'rgba(255,255,255,0.55)',

                                fontSize:
                                  '0.88rem',
                              }}
                            >
                              {formatCurrency(
                                item.unitAmountCents,
                                order.currency
                              )}{' '}
                              each
                            </Typography>
                          </Box>


                          <Typography
                            sx={{
                              color:
                                'white',

                              fontWeight:
                                800,

                              whiteSpace:
                                'nowrap',
                            }}
                          >
                            {formatCurrency(
                              item.unitAmountCents *
                              item.quantity,
                              order.currency
                            )}
                          </Typography>
                        </Stack>


                        {Array.isArray(
                          item
                            .fitmentSelections
                        ) &&
                          item
                            .fitmentSelections
                            .length >
                            0 && (
                          <Box
                            sx={{
                              mt:
                                1.5,

                              pl:
                                2,

                              borderLeft:
                                '2px solid rgba(0,255,170,0.25)',
                            }}
                          >
                            {item
                              .fitmentSelections
                              .map(
                                (
                                  fitment,
                                  fitmentIndex
                                ) => (
                                  <Typography
                                    key={
                                      fitmentIndex
                                    }
                                    sx={{
                                      color:
                                        'rgba(255,255,255,0.62)',

                                      fontSize:
                                        '0.87rem',
                                    }}
                                  >
                                    {fitment.label ||
                                      fitment.atgModel}
                                    {' × '}
                                    {fitment.quantity}
                                  </Typography>
                                )
                              )}
                          </Box>
                        )}
                      </Box>
                    )
                  )}
                </Stack>


                <Box
                  sx={{
                    mt:
                      3,

                    p:
                      2.5,

                    borderRadius:
                      '18px',

                    backgroundColor:
                      'rgba(255,255,255,0.045)',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{
                        color:
                          'rgba(255,255,255,0.68)',

                        fontWeight:
                          800,
                      }}
                    >
                      Total
                    </Typography>


                    <Typography
                      variant="h4"
                      sx={{
                        color:
                          'white',

                        fontWeight:
                          900,
                      }}
                    >
                      {formatCurrency(
                        order.amountTotalCents,
                        order.currency
                      )}
                    </Typography>
                  </Stack>
                </Box>


                {/* SHIPPING DESTINATION */}

                {order
                  .shippingDestination
                  ?.city && (
                  <>
                    <Divider
                      sx={{
                        my:
                          4,

                        borderColor:
                          'rgba(255,255,255,0.10)',
                      }}
                    />


                    <Typography
                      variant="h4"
                      sx={{
                        mb:
                          1,

                        color:
                          'white',

                        fontWeight:
                          900,
                      }}
                    >
                      Shipping Destination
                    </Typography>


                    <Typography
                      sx={{
                        color:
                          'rgba(255,255,255,0.70)',

                        lineHeight:
                          1.6,
                      }}
                    >
                      {order
                        .shippingDestination
                        .city}

                      {order
                        .shippingDestination
                        .state
                        ? `, ${order.shippingDestination.state}`
                        : ''}

                      {order
                        .shippingDestination
                        .country
                        ? `, ${order.shippingDestination.country}`
                        : ''}
                    </Typography>
                  </>
                )}


                {/* TRACKING */}

                {order
                  .fulfillment
                  ?.trackingNumber && (
                  <>
                    <Divider
                      sx={{
                        my:
                          4,

                        borderColor:
                          'rgba(255,255,255,0.10)',
                      }}
                    />


                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          color:
                            'white',

                          fontWeight:
                            900,

                          mb:
                            1.5,
                        }}
                      >
                        Tracking
                      </Typography>


                      <Typography
                        sx={{
                          color:
                            'rgba(255,255,255,0.60)',

                          fontSize:
                            '0.85rem',

                          fontWeight:
                            700,

                          textTransform:
                            'uppercase',

                          letterSpacing:
                            '0.06em',
                        }}
                      >
                        {order
                          .fulfillment
                          .carrier ||
                          'Carrier'}
                      </Typography>


                      <Typography
                        sx={{
                          mt:
                            0.5,

                          color:
                            'white',

                          fontWeight:
                            800,

                          fontSize:
                            '1.05rem',

                          wordBreak:
                            'break-word',
                        }}
                      >
                        {order
                          .fulfillment
                          .trackingNumber}
                      </Typography>


                      {order
                        .fulfillment
                        .trackingUrl && (
                        <Button
                          variant="contained"
                          component="a"
                          href={
                            order
                              .fulfillment
                              .trackingUrl
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={
                            <OpenInNewOutlined />
                          }
                          sx={{
                            mt:
                              2.5,

                            backgroundColor:
                              colors.greenAccent[400],

                            color:
                              '#06100c',

                            borderRadius:
                              '999px',

                            px:
                              3,

                            py:
                              1.25,

                            fontWeight:
                              900,

                            '&:hover': {
                              backgroundColor:
                                colors.greenAccent[300],
                            },
                          }}
                        >
                          Track Shipment
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </Paper>
        </Container>


        <Footer />
      </Box>
    );
  };


export default PublicOrderStatus;